import { z } from 'zod';
import { MANIFEST_ITEM_TYPES, ZONE_IDS } from '../constants.ts';
import { idSchema, unixMsSchema } from './common.ts';
import { fitSchema } from './playlist.ts';
import { orientationSchema } from './screen.ts';

// The manifest is the server-resolved answer to "what does this screen show now
// and for the next 24h" (docs/03 §manifest). The player never evaluates schedule
// rules — it just finds the timeline window containing `now` and loops its items.

// image | video | url | html. 'html' (Sprint 12) is a generated Content Engine document
// (html_template or html_snippet): a self-contained page served from the token-authed
// /v1/player/media/:id, so it is CACHED and rendered from a blob object-URL in the
// sandboxed iframe. 'url' stays reserved for a LIVE external page loaded as-is.
// MANIFEST_ITEM_DELIVERY declares that per-type rule; the player guard tests enforce it.
export const manifestItemTypeSchema = z.enum(MANIFEST_ITEM_TYPES);

export const manifestItemSchema = z.object({
  id: idSchema, // the playlist_item id (position in the playlist)
  type: manifestItemTypeSchema,
  url: z.string(), // relative /v1/player/media/:id (image/video/html) or an external URL for type=url
  duration_ms: z.number().int().positive(),
  fit: fitSchema,
  // The media ASSET id this item plays (Sprint 8). Additive: old players ignore it and
  // parse the id out of `url` — but that fails for type=url (external URL, no media path),
  // so proof-of-play attribution reads this field. Present on every item the current
  // resolver emits; optional in the schema so pre-Sprint-8 fixtures still validate.
  media_id: idSchema.optional(),
});
export type ManifestItem = z.infer<typeof manifestItemSchema>;

export const manifestWindowSchema = z.object({
  from: unixMsSchema,
  to: unixMsSchema,
  playlist_id: idSchema,
  items: z.array(manifestItemSchema),
});
export type ManifestWindow = z.infer<typeof manifestWindowSchema>;

// Overlay layer (docs/09 §C2) — an additive top layer above the timeline. Present only
// when the screen (or org default) has an overlay assigned; the `overlay` key is omitted
// entirely otherwise. `logo`/`ticker` are independently nullable (an overlay may be
// logo-only, ticker-only, or both). Contract is fixed — the player codes against it.
export const manifestOverlayLogoSchema = z.object({
  url: z.string(), // relative /v1/player/media/:id (org-chain-checked ready image)
  position: z.enum(['br', 'bl', 'tr', 'tl']),
  size_pct: z.number().int(),
  opacity: z.number().int(),
});

export const manifestOverlayTickerSchema = z.object({
  text: z.string(),
  speed_px_s: z.number().int().positive(),
});

export const manifestOverlaySchema = z.object({
  logo: manifestOverlayLogoSchema.nullable(),
  ticker: manifestOverlayTickerSchema.nullable(),
});
export type ManifestOverlay = z.infer<typeof manifestOverlaySchema>;

// Manifest v2 zones (Sprint 9, additive — docs/09 §C1). A screen with a non-fullscreen
// layout gets a `zones` array covering ALL its zones, including `main` (whose timeline
// is identical to the top-level `timeline` below — kept for old-player compatibility).
// A fullscreen layout (or no layout / a dangling layout_id) omits the `zones` key
// entirely, keeping the payload lean; old players ignore the unknown key regardless.
export const manifestZoneIdSchema = z.enum(ZONE_IDS);

export const manifestZoneRectSchema = z.object({
  x: z.number().int(),
  y: z.number().int(),
  w: z.number().int(),
  h: z.number().int(),
});

export const manifestZoneSchema = z.object({
  id: manifestZoneIdSchema,
  rect_pct: manifestZoneRectSchema,
  timeline: z.array(manifestWindowSchema),
});
export type ManifestZone = z.infer<typeof manifestZoneSchema>;

export const manifestSchema = z.object({
  version: z.string(), // ETag: "sha256-..."
  min_player_version: z.string(),
  screen: z.object({
    id: idSchema,
    orientation: orientationSchema,
    timezone: z.string(),
  }),
  poll_interval_s: z.number().int().positive(),
  valid_until: unixMsSchema, // resolver horizon (now + 24h)
  timeline: z.array(manifestWindowSchema), // consecutive windows, screen-TZ resolved
  fallback: z.object({
    items: z.array(manifestItemSchema),
  }),
  // Present only when an overlay is assigned (screen override or org default). Old
  // players ignore the unknown key; the key is omitted when there is no overlay.
  overlay: manifestOverlaySchema.optional(),
  // Present only for a resolved non-fullscreen layout (Sprint 9); see manifestZoneSchema.
  zones: z.array(manifestZoneSchema).optional(),
});
export type Manifest = z.infer<typeof manifestSchema>;
